import { cn, typographyVariants } from '@seen/ui'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type InputHTMLAttributes } from 'react'

const variants = cva(
  'flex h-10 w-full border-zinc-200 bg-transparent py-2 ring-offset-white file:border-0 file:font-medium file:text-zinc-950 placeholder:text-placeholder focus-visible:outline-hidden focus-visible:ring-zinc-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-800 dark:ring-offset-zinc-950 dark:focus-visible:ring-zinc-300 dark:file:text-zinc-50',
  {
    variants: {
      variant: {
        default: 'rounded-md border px-3 text-sm focus-visible:ring-2',
        underline:
          'h-auto border-b py-1.5 text-sm hover:border-zinc-400 focus-visible:border-zinc-950 dark:focus-visible:border-zinc-300',
        h1: cn(
          typographyVariants({ variant: 'h1' }),
          'hover:backdrop-brightness-95 focus-visible:backdrop-brightness-95'
        ),
        h2: cn(
          typographyVariants({ variant: 'h2' }),
          'hover:backdrop-brightness-95 focus-visible:backdrop-brightness-95'
        ),
        h3: cn(
          typographyVariants({ variant: 'h3' }),
          'hover:backdrop-brightness-95 focus-visible:backdrop-brightness-95'
        ),
        h4: cn(
          typographyVariants({ variant: 'h4' }),
          'hover:backdrop-brightness-95 focus-visible:backdrop-brightness-95'
        ),
        h5: cn(
          typographyVariants({ variant: 'h5' }),
          'hover:backdrop-brightness-95 focus-visible:backdrop-brightness-95'
        ),
        h6: cn(
          typographyVariants({ variant: 'h6' }),
          'hover:backdrop-brightness-95 focus-visible:backdrop-brightness-95'
        ),
        large:
          'font-semibold text-lg hover:backdrop-brightness-95 focus-visible:backdrop-brightness-95',
        'p-ui':
          'text-base leading-6 hover:backdrop-brightness-95 focus-visible:backdrop-brightness-95',
      },
      loading: {
        true: 'animate-pulse rounded-md border-0 bg-zinc-100 text-transparent placeholder:text-transparent dark:bg-zinc-800',
        false: '',
      },
    },
    compoundVariants: [
      {
        loading: true,
        variant: 'underline',
        className: 'border-0',
      },
    ],
    defaultVariants: {
      variant: 'default',
      loading: false,
    },
  }
)

export interface InputProps
  extends
    InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof variants> {}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant, loading, disabled, ...props }, ref) => (
    <input
      className={cn(variants({ variant, loading, className }))}
      ref={ref}
      disabled={loading ? true : disabled}
      {...props}
    />
  )
)
Input.displayName = 'Input'
